import {
  applyActionInPlace,
  createGame,
  getPlayerView,
  type Action,
  type ActionEnvelope,
  type GameConfig,
  type GameLog,
  type GameState,
  type PlayerId,
  type PlayerView,
  type Plan,
  type TutorialChapter,
} from '@il-colpo/engine';
import type { GameClient, GameMode, SendResult } from '../game/client.ts';

/**
 * A finished heist, played again from its log: the engine rebuilds it action by action, and the game
 * screens read it like any other game, through the view of one seat. Nothing can be done in it (every
 * action is refused); what can be done is to move in time, change the speed and change the seat the
 * heist is watched from. Moving forward applies the next actions; moving back rebuilds from the
 * start (a heist is a few thousand actions, most of them ticks of the clock).
 */

export const REPLAY_SPEEDS = [1, 4, 16, 64] as const;
export type ReplaySpeed = (typeof REPLAY_SPEEDS)[number];

/** How often the replay moves on, in real milliseconds. */
const FRAME_MS = 100;

export interface ReplayOptions {
  /** The seat the heist is watched from at first. */
  seat: PlayerId;
  /** The names to show, by seat (the log keeps seats, not names). */
  names: Readonly<Record<PlayerId, string>>;
}

export class ReplayClient implements GameClient {
  readonly mode: GameMode = 'replay';
  readonly chapter: TutorialChapter | null = null;
  readonly canPause = false;
  readonly config: GameConfig;

  private readonly actions: readonly ActionEnvelope[];
  /** Milliseconds of game time at the end of the first `i` actions: `at[i]`. */
  private readonly at: Float64Array;
  private state: GameState;
  private pos = 0;
  private seat: PlayerId;
  private speed: ReplaySpeed = 4;
  private running = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private ver = 0;
  private cachedFor = -1;
  private cached: PlayerView | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly log: GameLog,
    options: ReplayOptions,
  ) {
    this.config = {
      ...log.config,
      players: log.config.players.map((p) => ({ ...p, name: options.names[p.id] ?? p.name })),
    };
    this.actions = log.actions;
    this.at = new Float64Array(this.actions.length + 1);
    let clock = 0;
    this.actions.forEach((env, i) => {
      if (env.action.type === 'tick') clock += env.action.dtMs;
      this.at[i + 1] = clock;
    });
    this.seat = log.config.players.some((p) => p.id === options.seat)
      ? options.seat
      : (log.config.players[0]?.id as PlayerId);
    this.state = createGame(this.config, log.seed);
  }

  // ---- What the screens read ----------------------------------------------------------

  get humanId(): string {
    return this.seat;
  }

  lessonDone(): boolean {
    return false;
  }

  view(): PlayerView {
    if (this.cachedFor !== this.ver || this.cached === null) {
      this.cached = getPlayerView(this.state, this.seat);
      this.cachedFor = this.ver;
    }
    return this.cached;
  }

  version(): number {
    return this.ver;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private changed(): void {
    this.ver += 1;
    for (const l of [...this.listeners]) l();
  }

  // ---- Nothing can be done in a replay -------------------------------------------------

  send(_action: Action): SendResult {
    return { ok: false, error: { code: 'notAllowed', message: 'a replay cannot be played' } };
  }

  paused(): boolean {
    return false;
  }

  setPaused(): void {
    // The controls of a replay are its own.
  }

  suggestPlan(): Plan | null {
    return null;
  }

  autopilot(): boolean {
    return false;
  }

  dispose(): void {
    this.setPlaying(false);
    this.listeners.clear();
  }

  // ---- The controls of a replay --------------------------------------------------------

  /** How many actions the heist has, and how many have been applied. */
  get length(): number {
    return this.actions.length;
  }

  get position(): number {
    return this.pos;
  }

  /** Milliseconds of game time now, and in all. */
  get timeMs(): number {
    return this.at[this.pos] as number;
  }

  get durationMs(): number {
    return this.at[this.actions.length] as number;
  }

  get atEnd(): boolean {
    return this.pos >= this.actions.length;
  }

  get watching(): PlayerId {
    return this.seat;
  }

  get speedFactor(): ReplaySpeed {
    return this.speed;
  }

  get playing(): boolean {
    return this.running;
  }

  /** The seats there are to watch from. */
  get seats(): readonly PlayerId[] {
    return this.config.players.map((p) => p.id);
  }

  watchFrom(seat: PlayerId): void {
    if (seat === this.seat || !this.seats.includes(seat)) return;
    this.seat = seat;
    this.changed();
  }

  setSpeedFactor(speed: ReplaySpeed): void {
    this.speed = speed;
    this.changed();
  }

  setPlaying(on: boolean): void {
    if (on === this.running) return;
    if (on && this.atEnd) this.seek(0);
    this.running = on;
    if (on) {
      this.timer = setInterval(() => {
        this.advance(FRAME_MS * this.speed);
      }, FRAME_MS);
    } else if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.changed();
  }

  /** Moves on by `gameMs` of game time: every action that is not a tick comes for free. */
  advance(gameMs: number): void {
    let budget = gameMs;
    let moved = false;
    while (this.pos < this.actions.length) {
      const env = this.actions[this.pos] as ActionEnvelope;
      if (env.action.type === 'tick') {
        if (budget <= 0) break;
        budget -= env.action.dtMs;
      }
      applyActionInPlace(this.state, env);
      this.pos += 1;
      moved = true;
    }
    if (this.atEnd && this.running) {
      this.running = false;
      if (this.timer !== null) clearInterval(this.timer);
      this.timer = null;
      moved = true;
    }
    if (moved) this.changed();
  }

  /** Goes to a place in the heist (a number of actions): forward from here, or back from the start. */
  seek(position: number): void {
    const target = Math.min(Math.max(0, Math.floor(position)), this.actions.length);
    if (target === this.pos) return;
    if (target < this.pos) {
      this.state = createGame(this.config, this.log.seed);
      this.pos = 0;
    }
    while (this.pos < target) {
      applyActionInPlace(this.state, this.actions[this.pos] as ActionEnvelope);
      this.pos += 1;
    }
    this.changed();
  }

  /** Goes to a moment of game time. */
  seekTime(ms: number): void {
    // The first position whose clock has reached the moment.
    let lo = 0;
    let hi = this.actions.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if ((this.at[mid] as number) < ms) lo = mid + 1;
      else hi = mid;
    }
    this.seek(lo);
  }
}
